import React, { useState, useMemo } from 'react';
import { formatNumber, formatRank } from '../../utils/formatting';
import { getScoreColor } from '../../utils/coloring';
import { weightedPercentages } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import ScoreMethodologyModal from '../Modals/ScoreMethodologyModal';
import './Tables.css';

const LocationNameScoresTable = ({ selectedLocation, selectedCrime, compareMode }) => {
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);

  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  if (isNation) {
    return (
      <div className="loading">
        Scores are not available for the entire nation
      </div>
    );
  }

  const getScoreFieldKey = (prefix) => {
    if (!isCountyOrCity) return `${prefix}_score`;
    
    return compareMode === 'state' 
      ? `${prefix}_state_score` 
      : `${prefix}_national_score`;
  };
  
  const getScoreRankFieldKey = (prefix) => {
    if (!isCountyOrCity) return `${prefix}_score_rank`;
    
    return compareMode === 'state' 
      ? `${prefix}_state_score_rank` 
      : `${prefix}_national_score_rank`;
  };
  
  const getCompositeFieldKey = (baseField) => {
    if (!isCountyOrCity) return baseField;
    
    return compareMode === 'state'
      ? `state_${baseField}`
      : `national_${baseField}`;
  };

  const transformData = () => {
    const allCrimes = [
      {
        crimeType: 'All Crime',
        level: 0,
        score: current[getScoreFieldKey('all_crime')],
        rank: current[getScoreRankFieldKey('all_crime')]
      },
      {
        crimeType: 'Violent Crime',
        level: 1,
        score: current[getScoreFieldKey('violent_crime')],
        rank: current[getScoreRankFieldKey('violent_crime')]
      },
      {
        crimeType: 'Murder',
        level: 2,
        score: current[getScoreFieldKey('murder')],
        rank: current[getScoreRankFieldKey('murder')]
      },
      {
        crimeType: 'Rape',
        level: 2,
        score: current[getScoreFieldKey('rape')],
        rank: current[getScoreRankFieldKey('rape')]
      },
      {
        crimeType: 'Robbery',
        level: 2,
        score: current[getScoreFieldKey('robbery')],
        rank: current[getScoreRankFieldKey('robbery')]
      },
      {
        crimeType: 'Aggravated Assault',
        level: 2,
        score: current[getScoreFieldKey('aggravated_assault')],
        rank: current[getScoreRankFieldKey('aggravated_assault')]
      },
      {
        crimeType: 'Property Crime',
        level: 1,
        score: current[getScoreFieldKey('property_crime')],
        rank: current[getScoreRankFieldKey('property_crime')]
      },
      {
        crimeType: 'Burglary',
        level: 2,
        score: current[getScoreFieldKey('burglary')],
        rank: current[getScoreRankFieldKey('burglary')]
      },
      {
        crimeType: 'Larceny/Theft',
        level: 2,
        score: current[getScoreFieldKey('larceny_theft')],
        rank: current[getScoreRankFieldKey('larceny_theft')]
      },
      {
        crimeType: 'Motor Vehicle Theft',
        level: 2,
        score: current[getScoreFieldKey('motor_vehicle_theft')],
        rank: current[getScoreRankFieldKey('motor_vehicle_theft')]
      }
    ];
  
    return allCrimes;
  };

  const getWeight = (crimeType) => {
    const key = crimeType.toLowerCase().replace(/[\s/]/g, '_');
    const weight = weightedPercentages[key] || '-';
    
    const aggregateTypes = ['All Crime', 'Violent Crime', 'Property Crime'];
    if (aggregateTypes.includes(crimeType) && weight !== '-') {
      return (
        <>
          <span 
            className="footnote-link" 
            onClick={(e) => {
              e.stopPropagation();
              setIsMethodologyModalOpen(true);
            }}
          >
            {weight}<strong>*</strong>
          </span>
        </>
      );
    }
    
    return weight;
  };

  const renderRow = (item) => {
    const isSelected = item.crimeType.toLowerCase().replace(/[\s/]/g, '_') === selectedCrime?.toLowerCase();
  
    return (
      <tr key={item.crimeType} className={`level-${item.level}${isSelected ? ' selected-row' : ''}`}>
        <td className="crime-type-cell">
          <div className="crime-type-content">
            <span>{item.crimeType}</span>
          </div>
        </td>
        <td className="score-cell bold" style={{ color: getScoreColor(item.score) }}>
          <em>{formatNumber(item.score, true)}</em>
        </td>
        <td className="rank-cell">
          <em>{formatRank(item.rank)}</em>
        </td>
        <td className="weight-cell">{getWeight(item.crimeType)}</td>
      </tr>
    );
  };

  const compositeScoreField = getCompositeFieldKey('composite_score');
  const compositeScoreRankField = getCompositeFieldKey('composite_score_rank');

  const data = transformData();

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {isCountyOrCity ? compareMode === 'state' ? `${stateAbbr}` : 'US' : ''} KRIME Score&trade; (Composite):
          <span style={{ color: getScoreColor(current[compositeScoreField]) }}>
            <strong>&nbsp;{formatNumber(current[compositeScoreField], true)}</strong>
          </span>
          {current[compositeScoreField] != null && (
            <>
              &nbsp;({isCountyOrCity ? (compareMode === 'state' ? `${stateAbbr} ` : 'US ') : ''}{locationType} Rank:&nbsp;
              <strong>{formatRank(current[compositeScoreRankField])}</strong>) 
            </>
          )}
      </div>
      <div className="main-table-scroll-container location-name-table">
        <table className="main-table location-name-table scores-table">
          <thead>
            <tr>
              <th>Type of Crime</th>
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} Crime Score</div>
                  <div>(Non-Weighted)</div>
                </div>
              </th>
  
              <th>
                <div className="stacked-header">
                  <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                  <div>Rank</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>KRIME Score&trade;</div>
                  <div>Weight</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.map(item => renderRow(item))}
          </tbody>
        </table>
      </div>
      
      <ScoreMethodologyModal 
        isOpen={isMethodologyModalOpen} 
        onClose={() => setIsMethodologyModalOpen(false)} 
      />
    </div>
  );
};

export default LocationNameScoresTable;